// AddOrder.js - Admin-side order creation ("Nv Commande"), so production
// can be launched from an order staff entered directly (phone/walk-in sale,
// bulk/speculative run) and not only from a real B2B client checkout.
// Full cart-style builder: pick a client + magasin, add product/design
// lines (pack-mode or piece-mode, mirroring the B2B cart's own data shape),
// review the cart, fill delivery/billing info, submit.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { userRequest } from "../../RequestMethods";
import { getImageUrl } from "../../utils/imageProxyUtils";
import { getColorDisplayName } from "../../utils/colorNames";
import "../Stock/Stock.css";
import "./AddOrder.css";

const emptyDeliveryAddress = { address: "", city: "", postalCode: "", country: "" };

// getImageUrl prefers the full-size original, which is fine for a real
// product page but slow for a small config/cart preview - this prefers the
// smallest variant available instead, since these previews are shown at
// 110px (or smaller) anyway.
const getPreviewUrl = (img) => {
  if (!img) return null;
  if (typeof img.thumbnail === "string") return img.thumbnail;
  if (typeof img.medium === "string") return img.medium;
  return getImageUrl(img);
};

const getPriceAfterDiscount = (price = 0, discount = 0) =>
  Number(price) - (Number(price) * (Number(discount) || 0)) / 100;

const AddOrder = () => {
  const navigate = useNavigate();

  // ── Client / magasin ──────────────────────────────────────────────
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedMagasinId, setSelectedMagasinId] = useState("");

  // ── Product / design picker ──────────────────────────────────────
  const [itemTab, setItemTab] = useState("product"); // "product" | "design"
  const [products, setProducts] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [selectedItemId, setSelectedItemId] = useState("");
  const [itemDetail, setItemDetail] = useState(null); // full product, or { design, linkedProduct }
  const [itemLoading, setItemLoading] = useState(false);
  const [selectedColor, setSelectedColor] = useState("");
  const [packCount, setPackCount] = useState("");
  const [sizeQuantities, setSizeQuantities] = useState({});

  // ── Cart ──────────────────────────────────────────────────────────
  const [cart, setCart] = useState([]);

  // ── Delivery / billing / order fields ────────────────────────────
  const [company, setCompany] = useState("");
  const [siret, setSiret] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [number, setNumber] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState(emptyDeliveryAddress);
  const [billingSameAsDelivery, setBillingSameAsDelivery] = useState(true);
  const [billingAddress, setBillingAddress] = useState(emptyDeliveryAddress);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    userRequest.get("clients").then((res) => setClients(res.data.clients || []))
      .catch((err) => toast.error(err?.response?.data?.message || "Erreur lors du chargement des clients."));
    userRequest.get("products/dropdown", { params: { _: Date.now() } })
      .then((res) => setProducts((res.data.products || []).filter((p) => p.isPublishedAdmin !== false)))
      .catch((err) => toast.error(err?.response?.data?.message || "Erreur lors du chargement des produits."));
    userRequest.get("designs", { params: { limit: 500 } })
      .then((res) => setDesigns((res.data.designs || []).filter((d) => d.isPublishedAdmin !== false)))
      .catch((err) => toast.error(err?.response?.data?.message || "Erreur lors du chargement des modèles."));
  }, []);

  const selectedClient = clients.find((c) => c._id === selectedClientId) || null;
  const magasins = selectedClient?.magasins || [];

  const handleSelectClient = (clientId) => {
    setSelectedClientId(clientId);
    setSelectedMagasinId("");
    const client = clients.find((c) => c._id === clientId);
    const defaultMagasin = client?.magasins?.find((m) => m.isDefault) || client?.magasins?.[0];
    if (defaultMagasin) {
      applyMagasin(defaultMagasin);
      setSelectedMagasinId(defaultMagasin.id);
    } else if (client) {
      setCompany(client.companyName || "");
      setSiret(client.siret || "");
      setVatNumber(client.vatNumber || "");
      setNumber(client.number || "");
      setEmail(client.email || "");
    }
  };

  const applyMagasin = (magasin) => {
    setCompany(magasin.companyName || magasin.name || "");
    setSiret(magasin.siret || "");
    setVatNumber(magasin.vatNumber || "");
    setNumber(magasin.number || "");
    setEmail(magasin.email || "");
    const addr = magasin.address;
    if (addr && typeof addr === "object") {
      setDeliveryAddress({
        address: addr.street || "",
        city: magasin.city || addr.city || "",
        postalCode: magasin.postalCode || addr.postalCode || "",
        country: magasin.country || addr.country || "",
      });
    } else {
      setDeliveryAddress({
        address: addr || "",
        city: magasin.city || "",
        postalCode: magasin.postalCode || "",
        country: magasin.country || "",
      });
    }
  };

  const handleSelectMagasin = (magasinId) => {
    setSelectedMagasinId(magasinId);
    const magasin = magasins.find((m) => m.id === magasinId);
    if (magasin) applyMagasin(magasin);
  };

  const filteredProducts = useMemo(() => {
    const term = itemSearch.trim().toLowerCase();
    if (!term) return products;
    return products.filter((p) => (p.title || p._id || "").toLowerCase().includes(term));
  }, [products, itemSearch]);

  const filteredDesigns = useMemo(() => {
    const term = itemSearch.trim().toLowerCase();
    if (!term) return designs;
    return designs.filter((d) => (d.title || "").toLowerCase().includes(term));
  }, [designs, itemSearch]);

  const resetItemConfig = () => {
    setSelectedItemId("");
    setItemDetail(null);
    setSelectedColor("");
    setPackCount("");
    setSizeQuantities({});
  };

  const handleTabChange = (tab) => {
    setItemTab(tab);
    setItemSearch("");
    resetItemConfig();
  };

  const handleSelectItem = async (id) => {
    setSelectedItemId(id);
    setSelectedColor("");
    setPackCount("");
    setSizeQuantities({});
    if (!id) { setItemDetail(null); return; }

    setItemLoading(true);
    try {
      if (itemTab === "product") {
        const res = await userRequest.get(`products/${encodeURIComponent(id)}`);
        setItemDetail({ type: "product", product: res.data.product });
      } else {
        const design = designs.find((d) => d._id === id);
        const linkedProductId =
          typeof design?.linkedProduct === "object" ? design.linkedProduct?._id : design?.linkedProduct;
        const res = await userRequest.get(`products/${encodeURIComponent(linkedProductId)}`);
        setItemDetail({ type: "design", design, product: res.data.product });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors du chargement de l'article.");
      setItemDetail(null);
    } finally {
      setItemLoading(false);
    }
  };

  const activeProduct = itemDetail?.product || null;
  const isPackMode = activeProduct?.isFinishedProduct === false && Array.isArray(activeProduct?.pack) && activeProduct.pack.length > 0;
  const isFinished = activeProduct?.isFinishedProduct === true;
  const availableColors = activeProduct && !isFinished ? Object.keys(activeProduct.colorsAndSizes || {}) : [];
  const sizesForColor =
    isFinished
      ? Object.keys(activeProduct?.sizesOnly || {})
      : Object.keys((activeProduct?.colorsAndSizes || {})[selectedColor] || {});

  const unitPrice = itemDetail
    ? getPriceAfterDiscount(
        itemDetail.type === "design" ? itemDetail.design?.price : activeProduct?.price,
        itemDetail.type === "design" ? itemDetail.design?.discount : activeProduct?.discount
      )
    : 0;

  // A design has its own imgs (what's actually being ordered visually) -
  // prefer those over the linked product's, falling back to the product's
  // when the design itself has none (e.g. a plain finished product).
  const previewImgs = (itemDetail?.type === "design" ? itemDetail.design?.imgs : null)?.length
    ? itemDetail.design.imgs
    : activeProduct?.imgs || [];
  const frontImg = previewImgs.find((img) => img.type === "front") || previewImgs[0];
  const backImg = previewImgs.find((img) => img.type === "back");
  const frontImgUrl = getPreviewUrl(frontImg);

  const handleSizeQtyChange = (size, value) => {
    setSizeQuantities((prev) => ({ ...prev, [size]: value }));
  };

  const handleAddToCart = () => {
    if (!activeProduct) return;
    if (!isFinished && !selectedColor && availableColors.length > 0) {
      toast.error("Veuillez sélectionner une couleur.");
      return;
    }

    let line;
    if (isPackMode) {
      const packs = Number(packCount) || 0;
      if (packs <= 0) { toast.error("Veuillez indiquer un nombre de packs."); return; }
      const piecesPerPack = activeProduct.pack.reduce((s, q) => s + (Number(q) || 0), 0);
      line = {
        _id: `${itemDetail.type === "design" ? itemDetail.design._id : activeProduct._id}-${selectedColor}-${Date.now()}`,
        itemType: itemDetail.type === "design" ? "design" : "finished",
        designId: itemDetail.type === "design" ? itemDetail.design._id : null,
        productId: activeProduct._id,
        title: (itemDetail.type === "design" ? itemDetail.design.title : activeProduct.title) || activeProduct._id,
        imageUrl: frontImgUrl,
        color: selectedColor,
        side: "front",
        size: null,
        pack: activeProduct.pack,
        selectedSizes: null,
        quantity: packs,
        price: unitPrice,
        piecesPerPack,
      };
    } else {
      const selected = Object.fromEntries(
        Object.entries(sizeQuantities).filter(([, qty]) => Number(qty) > 0).map(([size, qty]) => [size, Number(qty)])
      );
      if (Object.keys(selected).length === 0) { toast.error("Veuillez indiquer au moins une quantité."); return; }
      const totalPieces = Object.values(selected).reduce((s, q) => s + q, 0);
      line = {
        _id: `${itemDetail.type === "design" ? itemDetail.design._id : activeProduct._id}-${selectedColor || "nc"}-${Date.now()}`,
        itemType: itemDetail.type === "design" ? "design" : "finished",
        designId: itemDetail.type === "design" ? itemDetail.design._id : null,
        productId: activeProduct._id,
        title: (itemDetail.type === "design" ? itemDetail.design.title : activeProduct.title) || activeProduct._id,
        imageUrl: frontImgUrl,
        color: isFinished ? "" : selectedColor,
        side: "front",
        size: null,
        pack: null,
        selectedSizes: selected,
        quantity: totalPieces,
        price: unitPrice,
        piecesPerPack: 1,
      };
    }

    setCart((prev) => [...prev, line]);
    resetItemConfig();
    setItemSearch("");
    toast.success("Article ajouté au panier.");
  };

  const handleRemoveCartLine = (id) => {
    setCart((prev) => prev.filter((line) => line._id !== id));
  };

  const getLineSubtotal = (line) => {
    const pieces = line.pack ? (line.piecesPerPack || 0) * line.quantity : line.quantity;
    return pieces * (Number(line.price) || 0);
  };

  const cartTotal = cart.reduce((sum, line) => sum + getLineSubtotal(line), 0);

  const handleSubmit = async () => {
    if (!selectedClientId) { toast.error("Veuillez sélectionner un client."); return; }
    if (!magasins.length ? false : !selectedMagasinId) { toast.error("Veuillez sélectionner un magasin."); return; }
    if (cart.length === 0) { toast.error("Le panier est vide."); return; }
    if (!deliveryAddress.address || !deliveryAddress.city || !deliveryAddress.country) {
      toast.error("Veuillez renseigner l'adresse de livraison.");
      return;
    }
    if (!number) { toast.error("Veuillez renseigner un numéro de téléphone."); return; }
    if (!siret || !vatNumber) { toast.error("SIRET et numéro de TVA requis."); return; }

    setSubmitting(true);
    try {
      const payload = {
        clientId: selectedClientId,
        magasinId: selectedMagasinId,
        company,
        siret,
        vatNumber,
        number,
        email,
        notes,
        address: deliveryAddress.address,
        city: deliveryAddress.city,
        postalCode: deliveryAddress.postalCode,
        country: deliveryAddress.country,
        deliveryAddress,
        shippingAddress: deliveryAddress,
        billingSameAsDelivery,
        billingAddress: billingSameAsDelivery ? deliveryAddress : billingAddress,
        total: cartTotal,
        products: cart.map(({ piecesPerPack, imageUrl, ...rest }) => rest),
      };

      const res = await userRequest.post("orders/admin", payload);
      toast.success("Commande créée avec succès.");
      setTimeout(() => navigate(`/orders`), 1200);
      void res;
    } catch (err) {
      // Validation failures come back as { message: "Validation failed",
      // errors: { field: "reason" } } - showing just `message` hides the
      // actual field-level reason (e.g. "SIRET must contain 14 digits"),
      // leaving no way to tell what to fix.
      const fieldErrors = err?.response?.data?.errors;
      const detail = fieldErrors ? Object.values(fieldErrors).join(" / ") : null;
      toast.error(
        detail || err?.response?.data?.message || err?.response?.data?.error || "Erreur lors de la création de la commande."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="AddOrder Stock">
      <h1>Nouvelle commande</h1>
      <p className="add-order-subtitle">
        Crée une commande directement depuis l'ERP (vente téléphonique, guichet, ou lancement de production) - sans passer par le B2B.
      </p>

      <section className="stock-section">
        <h3>1. Client</h3>
        <div className="add-order-row">
          <input
            type="text"
            placeholder="Rechercher un client..."
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
          />
          <select value={selectedClientId} onChange={(e) => handleSelectClient(e.target.value)}>
            <option value="">Sélectionner un client...</option>
            {clients
              .filter((c) => (c.companyName || c.email || "").toLowerCase().includes(clientSearch.trim().toLowerCase()))
              .map((c) => (
                <option key={c._id} value={c._id}>{c.companyName || c.email}</option>
              ))}
          </select>
          {magasins.length > 0 && (
            <select value={selectedMagasinId} onChange={(e) => handleSelectMagasin(e.target.value)}>
              <option value="">Sélectionner un magasin...</option>
              {magasins.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          )}
        </div>
      </section>

      <section className="stock-section">
        <h3>2. Ajouter des articles</h3>
        <div className="add-order-tabs">
          <button type="button" className={itemTab === "product" ? "active" : ""} onClick={() => handleTabChange("product")}>Produits</button>
          <button type="button" className={itemTab === "design" ? "active" : ""} onClick={() => handleTabChange("design")}>Modèles (designs)</button>
        </div>

        <div className="add-order-row">
          <input
            type="text"
            placeholder="Rechercher..."
            value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
          />
          <select value={selectedItemId} onChange={(e) => handleSelectItem(e.target.value)}>
            <option value="">Sélectionner un article...</option>
            {itemTab === "product"
              ? filteredProducts.map((p) => <option key={p._id} value={p._id}>{p.title || p._id}</option>)
              : filteredDesigns.map((d) => <option key={d._id} value={d._id}>{d.title}</option>)}
          </select>
        </div>

        {itemLoading && <div className="loading-container"><div className="loading-spinner-custom"></div></div>}

        {!itemLoading && activeProduct && (
          <div className="add-order-config">
            {(frontImg || backImg) && (
              <div className="add-order-preview-imgs">
                {frontImg && <img src={frontImgUrl} alt="Devant" loading="eager" />}
                {backImg && <img src={getPreviewUrl(backImg)} alt="Dos" loading="eager" />}
              </div>
            )}

            {!isFinished && (
              <div className="add-order-field">
                <label>Couleur</label>
                <div className="add-order-color-swatches">
                  {availableColors.map((color) => (
                    <button
                      type="button"
                      key={color}
                      className={`add-order-swatch${selectedColor === color ? " selected" : ""}`}
                      onClick={() => setSelectedColor(color)}
                      title={color}
                    >
                      <span className="add-order-swatch-dot" style={{ backgroundColor: color }} />
                      <span className="add-order-swatch-label">{getColorDisplayName(color)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isPackMode ? (
              <div className="add-order-field">
                <label>Nombre de packs</label>
                <input type="number" min="0" value={packCount} onChange={(e) => setPackCount(e.target.value)} placeholder="ex: 5" />
                <div className="field-note">Pack: {activeProduct.pack.join(" / ")} pièces par taille</div>
              </div>
            ) : (
              <div className="add-order-sizes">
                {(isFinished ? Object.keys(activeProduct.sizesOnly || {}) : sizesForColor).map((size) => (
                  <div key={size} className="add-order-size-item">
                    <label>{size}</label>
                    <input
                      type="number"
                      min="0"
                      value={sizeQuantities[size] || ""}
                      onChange={(e) => handleSizeQtyChange(size, e.target.value)}
                    />
                  </div>
                ))}
                {!isFinished && !selectedColor && <div className="field-note">Choisissez d'abord une couleur.</div>}
              </div>
            )}

            <div className="field-note">Prix unitaire: €{unitPrice.toFixed(2)}</div>
            <button type="button" className="action-icon-btn btn-primary" onClick={handleAddToCart}>Ajouter au panier</button>
          </div>
        )}
      </section>

      <section className="stock-section">
        <h3>3. Panier ({cart.length})</h3>
        {cart.length === 0 ? (
          <div className="field-note">Aucun article dans le panier.</div>
        ) : (
          <table className="add-order-cart-table">
            <thead>
              <tr>
                <th></th>
                <th>Article</th>
                <th>Couleur</th>
                <th>Détail</th>
                <th>Qté</th>
                <th>Sous-total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {cart.map((line) => (
                <tr key={line._id}>
                  <td>
                    {line.imageUrl
                      ? <img className="add-order-cart-thumb" src={line.imageUrl} alt={line.title} loading="lazy" />
                      : <div className="add-order-cart-thumb add-order-cart-thumb-empty" />}
                  </td>
                  <td>{line.title}</td>
                  <td>{line.color ? getColorDisplayName(line.color) : "—"}</td>
                  <td>{line.pack ? `${line.quantity} pack(s)` : Object.entries(line.selectedSizes || {}).map(([s, q]) => `${s}:${q}`).join(", ")}</td>
                  <td>{line.pack ? (line.piecesPerPack || 0) * line.quantity : line.quantity}</td>
                  <td>€{getLineSubtotal(line).toFixed(2)}</td>
                  <td><button type="button" className="action-icon-btn btn-delete" onClick={() => handleRemoveCartLine(line._id)}>&times;</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {cart.length > 0 && <div className="add-order-total">Total: €{cartTotal.toFixed(2)}</div>}
      </section>

      <section className="stock-section">
        <h3>4. Livraison & facturation</h3>
        <div className="add-order-grid">
          <div className="add-order-field"><label>Société</label><input type="text" value={company} onChange={(e) => setCompany(e.target.value)} /></div>
          <div className="add-order-field"><label>SIRET</label><input type="text" value={siret} onChange={(e) => setSiret(e.target.value)} /></div>
          <div className="add-order-field"><label>N° TVA</label><input type="text" value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} /></div>
          <div className="add-order-field"><label>Téléphone</label><input type="text" value={number} onChange={(e) => setNumber(e.target.value)} /></div>
          <div className="add-order-field"><label>Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        </div>

        <h4>Adresse de livraison</h4>
        <div className="add-order-grid">
          <div className="add-order-field"><label>Adresse</label><input type="text" value={deliveryAddress.address} onChange={(e) => setDeliveryAddress({ ...deliveryAddress, address: e.target.value })} /></div>
          <div className="add-order-field"><label>Ville</label><input type="text" value={deliveryAddress.city} onChange={(e) => setDeliveryAddress({ ...deliveryAddress, city: e.target.value })} /></div>
          <div className="add-order-field"><label>Code postal</label><input type="text" value={deliveryAddress.postalCode} onChange={(e) => setDeliveryAddress({ ...deliveryAddress, postalCode: e.target.value })} /></div>
          <div className="add-order-field"><label>Pays</label><input type="text" value={deliveryAddress.country} onChange={(e) => setDeliveryAddress({ ...deliveryAddress, country: e.target.value })} /></div>
        </div>

        <label className="add-order-checkbox">
          <input type="checkbox" checked={billingSameAsDelivery} onChange={(e) => setBillingSameAsDelivery(e.target.checked)} />
          Facturation identique à la livraison
        </label>

        {!billingSameAsDelivery && (
          <div className="add-order-grid">
            <div className="add-order-field"><label>Adresse</label><input type="text" value={billingAddress.address} onChange={(e) => setBillingAddress({ ...billingAddress, address: e.target.value })} /></div>
            <div className="add-order-field"><label>Ville</label><input type="text" value={billingAddress.city} onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })} /></div>
            <div className="add-order-field"><label>Code postal</label><input type="text" value={billingAddress.postalCode} onChange={(e) => setBillingAddress({ ...billingAddress, postalCode: e.target.value })} /></div>
            <div className="add-order-field"><label>Pays</label><input type="text" value={billingAddress.country} onChange={(e) => setBillingAddress({ ...billingAddress, country: e.target.value })} /></div>
          </div>
        )}

        <div className="add-order-field">
          <label>Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        </div>
      </section>

      <button type="button" className="action-icon-btn btn-primary add-order-submit" disabled={submitting} onClick={handleSubmit}>
        {submitting ? "Création..." : "Créer la commande"}
      </button>

      <ToastContainer position="top-right" style={{ zIndex: 999999 }} />
    </div>
  );
};

export default AddOrder;
